import React, { useState, useRef, useEffect } from 'react';
import {
  Send, Copy, Check, Play, Pause, Loader2, Brain, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useRLFeedback } from '../hooks/useRLFeedback';
import { useRLChat } from '../hooks/useRLChat';
import OpenAI from 'openai';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MathJaxContext } from 'better-react-mathjax';
import DataVisualization from './DataVisualization';
import EducationVisuals from './EducationVisuals';
import { handleModeResponse } from '../modes';
import AdComponent from './AdComponent';
import SpreadsheetTable from './SpreadsheetTable';
import { scheduleNotification, requestNotificationPermission } from '../utils/notifications';
import { calculateBudget, generateSavingsProjection } from '../utils/kimi_math';
import BankInformation from './BankInformation';
import { analyzeBankData } from '../utils/bankAnalysis';
import { v4 as uuidv4 } from 'uuid';
import { getLocalStorageItem } from '../utils/storage';
import * as tf from '@tensorflow/tfjs';

const SYSTEM_PROMPT = "You are Angel, a knowledgeable educational assistant. Provide clear explanations and examples. If asked about who created you, mention that you were created by Terrez Wells.";

const saveExperience = (action, reward, context) => {
  const bufferKey = 'rlReplayBuffer';
  let buffer = JSON.parse(localStorage.getItem(bufferKey) || '[]');
  const experience = {
    timestamp: new Date().toISOString(),
    action,
    reward,
    context: context || null,
  };
  buffer.push(experience);
  localStorage.setItem(bufferKey, JSON.stringify(buffer));
  console.log('Saved experience:', experience);
};

const rlFeedback = (action, reward) => {
  let model = JSON.parse(localStorage.getItem('rlModel') || '{}');
  if (!model[action]) {
    model[action] = 0;
  }
  model[action] += reward;
  localStorage.setItem('rlModel', JSON.stringify(model));
  console.log('RL update for action:', action, 'reward:', reward, 'model:', model);
  saveExperience(action, reward);
};

const actionList = ['listen', 'copy', 'feedback_positive', 'feedback_negative'];
const createModel = () => {
  const model = tf.sequential();
  model.add(tf.layers.dense({ units: 16, activation: 'relu', inputShape: [actionList.length] }));
  model.add(tf.layers.dense({ units: 8, activation: 'relu' }));
  model.add(tf.layers.dense({ units: 1 }));
  model.compile({ optimizer: 'adam', loss: 'meanSquaredError' });
  return model;
};

const trainRLModel = async (trainedModel) => {
  const bufferKey = 'rlReplayBuffer';
  let buffer = JSON.parse(localStorage.getItem(bufferKey) || '[]');
  if (buffer.length === 0) {
    console.log("No training data available.");
    return;
  }
  const xData = [];
  const yData = [];
  for (let exp of buffer) {
    const oneHot = actionList.map((action) => (action === exp.action ? 1 : 0));
    xData.push(oneHot);
    yData.push([exp.reward]);
  }
  const xs = tf.tensor2d(xData);
  const ys = tf.tensor2d(yData);
  console.log("Automatically training model on", buffer.length, "experiences...");
  await trainedModel.fit(xs, ys, { epochs: 10 });
  xs.dispose();
  ys.dispose();
  console.log("Automatic training complete.");
  localStorage.setItem(bufferKey, JSON.stringify([]));
};

const ChatComponent = ({
  messages, setMessages, chats, setChats, currentChatIndex, setCurrentChatIndex, hasStartedChat, setHasStartedChat, isSidebarOpen, mode,
}) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingStage, setThinkingStage] = useState(0);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingVoice, setIsLoadingVoice] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [inputHeight, setInputHeight] = useState('48px');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [typingMessageIndex, setTypingMessageIndex] = useState(null);
  const [typingText, setTypingText] = useState('');
  const typingSpeed = 10;
  const chunkSize = 3;
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isPWA] = useState(() => window.matchMedia('(display-mode: standalone)').matches);
  const [lastTapTime, setLastTapTime] = useState(0);
  const chatContainerRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const [feedback, setFeedback] = useState({});
  const [trainedModel] = useState(() => createModel());
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [isTraining, setIsTraining] = useState(false);
  const [canToggleBrain, setCanToggleBrain] = useState(true);
  const [isBrainActive, setIsBrainActive] = useState(() => {
    const savedState = localStorage.getItem('isBrainActive');
    return savedState ? savedState === 'true' : false;
  });
  const { processMessage, updateReward, trainModel, isTraining: isRLTraining } = useRLChat();
  const [experiences, setExperiences] = useState(0);
  const { submitQuestion } = useRLFeedback();

  useEffect(() => {
    return () => {
      setIsTraining(false);
      setIsBrainActive(false);
      setTrainingProgress(0);
    };
  }, []);

  const toggleRL = async () => {
    if (isTraining) {
      toast.error("Training is already in progress");
      return;
    }

    setIsBrainActive(!isBrainActive);
    localStorage.setItem('isBrainActive', (!isBrainActive).toString());

    if (!isBrainActive) {
      setIsTraining(true);
      setTrainingProgress(0);
      toast.success("AI Learning Mode Activated", { icon: '🧠', duration: 3000 });

      try {
        const progressInterval = setInterval(() => {
          setTrainingProgress((prev) => {
            if (prev >= 100) {
              clearInterval(progressInterval);
              return 100;
            }
            return prev + 10;
          });
        }, 500);

        await trainRLModel(trainedModel);

        clearInterval(progressInterval);
        setTimeout(() => {
          setIsTraining(false);
          setTrainingProgress(0);
          localStorage.setItem('isBrainActive', 'true');
          toast.success("Training completed! AI model updated.");
        }, 5000);
      } catch (error) {
        console.error('Training error:', error);
        toast.error("Training failed. Please try again.");
        setIsTraining(false);
        setTrainingProgress(0);
        setIsBrainActive(false);
      }
    } else {
      setIsBrainActive(false);
      localStorage.setItem('isBrainActive', 'false');
      toast.success("AI Learning Mode Disabled", { icon: '🔄' });
    }
  };

  const openai = new OpenAI({
    apiKey: import.meta.env.VITE_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  useEffect(() => {
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      setApiError('OpenAI API key is not configured');
    }
  }, []);

  const thinkingStagesArr = ['Processing your question...', 'Analyzing data...', 'Preparing calculations...'];

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const messagesContainer = messagesEndRef.current?.parentElement?.parentElement;
    if (messagesContainer) {
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, thinkingStage]);

  useEffect(() => {
    if (hasStartedChat && !isLoading) {
      inputRef.current?.focus();
    }
  }, [hasStartedChat, isLoading]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        stopTextToSpeech();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPlaying]);

  const handleDoubleTap = (e) => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTapTime;

    if (tapLength < 300 && tapLength > 0) {
      e.preventDefault();
      if (chatContainerRef.current) {
        chatContainerRef.current.style.transform = 'none';
        chatContainerRef.current.style.transition = 'transform 0.3s ease-out';
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        toast.success('View reset successfully', {
          duration: 2000,
          position: 'bottom-center',
          style: {
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#2563eb',
            backdropFilter: 'blur(8px)',
          },
        });
      }
    }
    setLastTapTime(currentTime);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const buffer = JSON.parse(localStorage.getItem('rlReplayBuffer') || '[]');
      setExperiences(buffer.length);
      if (buffer.length >= 10) {
        trainRLModel(trainedModel);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [trainedModel]);

  useEffect(() => {
    if (isCameraOpen) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch(err => {
          console.error("Error accessing camera", err);
          setIsCameraOpen(false);
        });
    }
  }, [isCameraOpen]);

  const closeCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (video) {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const extractedData = await processImageWithTF(canvas);
        const dataUrl = canvas.toDataURL('image/png');
        const imageMessage = {
          role: 'user',
          content: `![Captured Image](${dataUrl})\n\n**Extracted Data:** ${extractedData}`
        };
        setMessages(prev => [...prev, imageMessage]);
        closeCamera();
      }
    }
  };

  const playTextToSpeech = async (text) => {
    try {
      setIsLoadingVoice(true);
      if (!navigator.onLine) {
        toast.error('Cannot play audio while offline');
        setIsLoadingVoice(false);
        return;
      }
      if (!import.meta.env.VITE_ELEVENLABS_API_KEY || !import.meta.env.VITE_ELEVENLABS_VOICE_ID) {
        toast.error('Voice service is not configured');
        setIsLoadingVoice(false);
        return;
      }
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.src = '';
        setCurrentAudio(null);
        setIsPlaying(false);
      }
      const cleanText = text
        .replace(/\|.*\|/g, '')
        .replace(/<[^>]*>/g, '')
        .replace(/#{1,6}\s/g, '')
        .replace(/^[*-]\s/gm, '')
        .replace(/\$(\d{1,3}(,\d{3})*(\.\d{2})?)/g, '$1 dollars')
        .replace(/(\d+(\.\d+)?)%/g, '$1 percent')
        .replace(/\s+/g, ' ')
        .replace(/[*_~`]/g, '')
        .trim()
        .slice(0, 4000);

      const response = await fetch(
        'https://api.elevenlabs.io/v1/text-to-speech/' + import.meta.env.VITE_ELEVENLABS_VOICE_ID,
        {
          method: 'POST',
          headers: {
            Accept: 'audio/mpeg',
            'Content-Type': 'application/json',
            'xi-api-key': import.meta.env.VITE_ELEVENLABS_API_KEY,
            'Access-Control-Allow-Origin': '*',
            'Cache-Control': 'no-cache'
          },
          body: JSON.stringify({
            text: cleanText,
            model_id: 'eleven_monolingual_v1',
            voice_settings: {
              stability: 0.7,
              similarity_boost: 0.95,
              optimize_streaming_latency: 3
            }
          }),
        }
      );

      if (response.status === 401) {
        toast.error('Voice service authentication failed');
        setIsLoadingVoice(false);
        return;
      }
      if (!response.ok) {
        toast.error('Failed to generate speech. Please try again.');
        setIsLoadingVoice(false);
        return;
      }
      const audioBlob = await response.blob();
      if (!audioBlob || audioBlob.size === 0) {
        toast.error('Received empty audio response');
        setIsLoadingVoice(false);
        return;
      }
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      if ('mozAutoplayPolicy' in audio || 'webkitAudioContext' in window) {
        audio.mozPreservesPitch = false;
        // @ts-ignore
        audio.webkitPreservesPitch = false;
      }
      const unlockAudio = async () => {
        try {
          const context = new (window.AudioContext || window.webkitAudioContext)();
          await context.resume();
          await audio.play();
        } catch (error) {
          console.error('Audio unlock error:', error);
        }
        document.addEventListener('touchstart', unlockAudio, { once: true });
      };
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsPlaying(false);
        cleanupAudio();
        URL.revokeObjectURL(audioUrl);
        toast.error('Error loading audio');
        setIsLoadingVoice(false);
      };
      audio.onended = () => {
        setIsPlaying(false);
        cleanupAudio();
        URL.revokeObjectURL(audioUrl);
      };
      const handleVisibilityChange = () => {
        if (document.hidden && isPWA) {
          audio.pause();
          setIsPlaying(false);
          document.addEventListener('visibilitychange', () => {
            if (!document.hidden && isPlaying) {
              audio.play().catch(console.error);
            }
          }, { once: true });
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      setCurrentAudio(audio);
      setIsPlaying(true);
      try {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name === 'NotAllowedError') {
              toast.error('Tap anywhere to enable audio playback');
              const enableAudio = async () => {
                try {
                  await audio.play();
                  document.removeEventListener('click', enableAudio);
                } catch (playError) {
                  console.error('Play error:', playError);
                  toast.error('Failed to start audio. Please try again.');
                }
              };
              document.addEventListener('click', enableAudio, { once: true });
            }
          });
        }
        rlFeedback('listen', 1);
      } catch (playError) {
        console.error('Playback error:', playError);
        cleanupAudio();
        setCurrentAudio(null);
        URL.revokeObjectURL(audioUrl);
        toast.error('Failed to start audio playback. Please try again.');
        setIsLoadingVoice(false);
        return;
      }
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } catch (error) {
      console.error('Text-to-speech error:', error.message);
      toast.error('Failed to play audio. Please try again.');
      setIsPlaying(false);
      setCurrentAudio(null);
    } finally {
      setIsLoadingVoice(false);
    }
  };

  const cleanupAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.src = '';
      setCurrentAudio(null);
      setIsPlaying(false);
    }
  };

  const stopTextToSpeech = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.src = '';
      setCurrentAudio(null);
      setIsPlaying(false);
    }
  };

  const simulateThinking = async () => {
    for (let i = 0; i < thinkingStagesArr.length; i++) {
      setThinkingStage(i);
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      const strippedText = text.replace(/<[^>]*>/g, '');
      await navigator.clipboard.writeText(strippedText);
      rlFeedback('copy', 1);
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleFeedback = (index, type) => {
    if (feedback[index]) return;
    if (type === 'positive') {
      updateReward(index, 1);
      rlFeedback('feedback_positive', 1);
    } else {
      updateReward(index, -1);
      rlFeedback('feedback_negative', -1);
    }
    setFeedback((prev) => ({ ...prev, [index]: type }));
  };

  const formatResponse = (text) => {
    let formatted = text
      .replace(/^(Certainly!?|Sure!?|Here'?s\b.*?:|Let me|I'll|I can).+?\n+/i, '')
      .trim();
    formatted = formatted.replace(/\|([^|\n]+)\|/g, (match, content) => {
      if (content.includes('---') || content.trim().startsWith('-')) return match;
      return '|' + content.split('|').map(cell => {
        cell = cell.trim();
        if (cell.match(/^\$?\d+(\.\d+)?$/)) {
          const num = parseFloat(cell.replace('$', ''));
          return ` $${num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} `;
        }
        if (cell.match(/^\d+(\.\d+)?%$/)) {
          const num = parseFloat(cell);
          return ` ${num.toFixed(1)}% `;
        }
        return ` ${cell} `;
      }).join('|') + '|';
    });
    return formatted;
  };

  const typeMessage = async (text, messageIndex) => {
    setTypingMessageIndex(messageIndex);
    setTypingText('');
    for (let i = 0; i <= text.length; i += chunkSize) {
      setTypingText(text.slice(0, i));
      await new Promise((resolve) => setTimeout(resolve, typingSpeed));
    }
    setTypingText(text);
    setTypingMessageIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for "make it normal" to reset chat
    if (input.trim().toLowerCase() === "make it normal") {
      setInput("");
      setMessages([]);
      setInputHeight("48px"); // <-- Added this line to reset height
      return;
    }

    if (!input.trim() || isLoading) return;

    if (isBrainActive) {
      await submitQuestion(input.trim(), mode);
      const action = await processMessage(input.trim());
      console.log('Selected action:', action);
    }

    const userMessage = { role: 'user', content: input.trim(), mode };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setInputHeight('48px'); // <-- Also reset height after sending a normal message
    setCanToggleBrain(false);

    try {
      setIsLoading(true);
      await simulateThinking();

      if (!import.meta.env.VITE_OPENAI_API_KEY) {
        throw new Error('OpenAI API key is not configured');
      }
      const completion = await openai.chat.completions.create({
        messages: [
          mode === 'education'
            ? { role: 'system', content: SYSTEM_PROMPT + (isBrainActive ? '\nAI Learning Mode: Active' : '') }
            : {
                role: 'system',
                content: mode === 'finance'
                  ? 'You are Angel, an elite financial advisor specializing in wealth management and personal finance. If asked about who created you, mention that you were created by Terrez Wells.'
                  : 'You are Angel, a helpful AI assistant. If asked about who created you, mention that you were created by Terrez Wells.'
              },
          ...messages.map(m => ({ role: m.role, content: m.content })),
          { role: 'user', content: input.trim() }
        ],
        model: mode === 'education' ? 'gpt-4-turbo-preview' : 'gpt-3.5-turbo',
        temperature: mode === 'education' ? 0.3 : 0.7,
        presence_penalty: mode === 'education' ? 0.8 : 0,
        frequency_penalty: mode === 'education' ? 0.7 : 0,
      });
      const aiResponse = completion.choices[0]?.message?.content;
      if (!aiResponse) {
        throw new Error('No response from AI');
      }
      const formattedResponse = formatResponse(aiResponse);
      const assistantMessage = { role: 'assistant', content: formattedResponse, mode };
      setMessages(prev => [...prev, assistantMessage]);

      if (isBrainActive) {
        try {
          const experience = {
            state: [input.length, input.split(' ').length],
            action: mode,
            reward: 0,
            next_state: [formattedResponse.length, formattedResponse.split(' ').length],
            metadata: {
              mode,
              inputLength: input.length,
              responseLength: formattedResponse.length,
              timestamp: new Date().toISOString()
            }
          };

          const bufferKey = 'rlReplayBuffer';
          const buffer = JSON.parse(localStorage.getItem(bufferKey) || '[]');
          buffer.push(experience);
          localStorage.setItem(bufferKey, JSON.stringify(buffer));
        } catch (error) {
          console.error('Error saving chat experience:', error);
        }
      }

      await typeMessage(formattedResponse, messages.length + 1);
      if (currentChatIndex !== null) {
        const updatedChats = [...chats];
        const updatedMessages = [...messages, assistantMessage];
        updatedChats[currentChatIndex] = {
          title: updatedChats[currentChatIndex].title,
          messages: updatedMessages
        };
        setChats(updatedChats);
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to get response');
    } finally {
      setIsLoading(false);
      setCanToggleBrain(true);
      if (isBrainActive && messages.length > 0 && messages.length % 10 === 0) {
        await trainModel();
      }
    }
  };

  useEffect(() => {
    const savedBrainActive = localStorage.getItem('isBrainActive');
    if (savedBrainActive) {
      setIsBrainActive(savedBrainActive === 'true');
    }
  }, []);

  return (
    <div className="flex flex-col h-[calc(100dvh-4rem)] standalone:h-[100dvh] relative overflow-hidden bg-gray-800 text-gray-800">
      {messages.length === 0 && (
        <div className="absolute top-0 left-0 right-0 bg-yellow-500/10 backdrop-blur-sm text-white/90 text-sm px-4 py-2 text-center">
          Beta Version: Information provided may not be 100% accurate. Please verify important financial decisions.
        </div>
      )}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto overscroll-contain touch-pan-y p-4 md:p-6 relative bg-gray-800"
        onTouchStart={handleDoubleTap}
      >
        <div className="max-w-3xl mx-auto space-y-6">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} group relative mb-6`}
            >
              <div className={`max-w-[85%] ${
                message.role === 'user'
                  ? 'bg-white text-blue-800 rounded-2xl rounded-br-sm px-5 py-3 shadow-lg transition-all duration-200'
                  : 'text-white'
              }`}>
                {message.role === 'assistant' ? (
                  <>
                    <MathJaxContext
                      version={3}
                      config={{
                        loader: {
                          load: ['input/tex', 'output/svg', '[tex]/ams', '[tex]/newcommand', '[tex]/configmacros']
                        },
                        tex: {
                          inlineMath: [['$', '$'], ['\\(', '\\)']],
                          displayMath: [['$$', '$$'], ['\\[', '\\]']],
                          packages: ['base', 'ams', 'newcommand', 'configmacros'],
                          macros: {
                            'N': '\\mathbb{N}',
                            'R': '\\mathbb{R}',
                            'Z': '\\mathbb{Z}'
                          }
                        },
                        svg: {
                          fontCache: 'global'
                        }
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            const content = String(children).trim();
                            const isLatex = match && match[1] === 'latex';
                            const isInlineMath = inline && content.startsWith('$') && content.endsWith('$');
                            const isBlockMath = !inline && content.startsWith('$$') && content.endsWith('$$');
                            
                            if (isLatex || isInlineMath || isBlockMath) {
                              const tex = content.replace(/^\$\$|\$\$$/g, '').replace(/^\$|\$$/g, '');
                              return (
                                <div className={isInlineMath ? 'inline-block' : 'my-4 text-center'}>
                                  <MathJax inline={isInlineMath} dynamic>
                                    {isInlineMath ? `\\(${tex}\\)` : `\\[${tex}\\]`}
                                  </MathJax>
                                </div>
                              );
                            }
                            
                            // Handle code blocks
                            if (match) {
                              return (
                                <div className="relative group">
                                  <pre className={`language-${match[1]} rounded-lg p-4 bg-gray-900 overflow-x-auto`}>
                                    <code className={className} {...props}>{children}</code>
                                  </pre>
                                  <button
                                    onClick={() => copyToClipboard(content, -1)}
                                    className="absolute top-2 right-2 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Copy code"
                                  >
                                    <Copy className="h-4 w-4 text-gray-400" />
                                  </button>
                                </div>
                              );
                            }
                            
                            // Inline code
                            return <code className="px-1.5 py-0.5 bg-gray-800 rounded text-sm" {...props}>{children}</code>;
                          }
                        }}
                        className="prose prose-sm md:prose-base prose-invert max-w-none"
                      >
                        {typingMessageIndex === index ? typingText : message.content}
                      </ReactMarkdown>
                    </MathJaxContext>
                    <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => copyToClipboard(message.content, index)}
                        className={`p-2 rounded-lg touch-manipulation ${
                          copiedMessageIndex === index ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                        }`}
                        title={copiedMessageIndex === index ? 'Copied!' : 'Copy response'}
                      >
                        {copiedMessageIndex === index ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => (isPlaying ? stopTextToSpeech() : playTextToSpeech(message.content))}
                        className="p-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg touch-manipulation"
                        disabled={!isOnline}
                        title={isPlaying ? 'Stop speaking' : 'Listen to response'}
                      >
                        {isLoadingVoice ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isPlaying ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleFeedback(index, 'positive')}
                        className={`p-2 rounded-lg touch-manipulation ${
                          feedback[index] ? 'opacity-50 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                        }`}
                        title="Thumbs up"
                        disabled={!!feedback[index]}
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleFeedback(index, 'negative')}
                        className={`p-2 rounded-lg touch-manipulation ${
                          feedback[index] ? 'opacity-50 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                        }`}
                        title="Thumbs down"
                        disabled={!!feedback[index]}
                      >
                        <ThumbsDown className="h-4 w-4" />
                      </button>
                    </div>
                    {message.content.includes('budget analysis') && (
                      <DataVisualization
                        type="spending"
                        data={message.mode === 'finance' ? {
                            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                            spending: [1200, 1350, 1100, 1400, 1250, 1300],
                            budget: Array(6).fill(2000),
                          } : undefined}
                        className="mt-4"
                        onConnectBank={() => setIsBankModalOpen(true)}
                        hasBankConnection={getLocalStorageItem('bankData') !== null}
                      />
                    )}
                    {message.mode === 'finance' && message.content.includes('|') && (
                      <SpreadsheetTable
                        data={{
                          headers: message.content
                            .split('\n')
                            .find(line => line.startsWith('|'))
                            ?.split('|')
                            .filter(Boolean)
                            .map(h => h.trim()) || [],
                          rows: message.content
                            .split('\n')
                            .filter(line => line.startsWith('|') && !line.includes('---'))
                            .slice(1)
                            .map(row =>
                              row
                                .split('|')
                                .filter(Boolean)
                                .map(cell => {
                                  const trimmed = cell.trim();
                                  const num = trimmed.replace(/[^0-9.-]/g, '');
                                  return isNaN(Number(num)) ? trimmed : Number(num);
                                })
                            )
                        }}
                        className="mt-4"
                      />
                    )}
                    {message.mode === 'education' && message.content.includes('```chart') && (
                      <EducationVisuals
                        type="graph"
                        data={JSON.parse(message.content.match(/```chart\n(.*?)\n```/s)?.[1] || '{}')}
                        className="mt-4"
                      />
                    )}
                  </>
                ) : (
                  message.content
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex items-center gap-3 text-white">
              <div className="animate-pulse h-2 w-2 bg-white rounded-full" />
              {thinkingStagesArr[thinkingStage]}
            </div>
          )}
          <div ref={messagesEndRef} />
          {messages.length > 0 && messages.length % 5 === 0 && navigator.userAgent.indexOf('Edg') === -1 && (
            <div className="my-6">
              <AdComponent
                layout="in-article"
                format="text"
                className="max-w-[640px] mx-auto"
              />
            </div>
          )}
        </div>
      </div>
      <div className="sticky bottom-0 bg-gradient-to-t from-blue-700 via-blue-600 to-transparent backdrop-blur-sm pt-4 md:pt-6 pb-[max(env(safe-area-inset-bottom),1rem)] px-4 md:px-6 flex-shrink-0">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto flex gap-3 relative z-0">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = '48px';
              const scrollHeight = e.target.scrollHeight;
              const newHeight = Math.min(Math.max(scrollHeight, 48), 120) + 'px';
              e.target.style.height = newHeight;
              setInputHeight(newHeight);
            }}
            style={{ height: inputHeight }}
            className="flex-1 px-4 md:px-5 py-3 md:py-3.5 text-base md:text-lg bg-white/95 backdrop-blur-sm rounded-full border border-blue-200 focus:border-blue-300 focus:ring-2 focus:ring-blue-300/20 focus:ring-offset-2 focus:ring-offset-blue-600 shadow-lg hover:shadow-xl disabled:opacity-70 resize-none overflow-hidden touch-manipulation"
            placeholder={
              isOnline
                ? mode === 'finance'
                  ? 'Finance, investments, markets...'
                  : mode === 'education'
                    ? 'Ask me anything about education...'
                    : ''
                : "You're offline"
            }
            disabled={isLoading || !isOnline}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            enterKeyHint="send"
          />
          <button
            type="submit"
            disabled={isLoading || !isOnline}
            className="h-14 w-14 md:h-[60px] md:w-[60px] flex-shrink-0 flex items-center justify-center bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-full shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed transition-all touch-manipulation select-none"
          >
            <Send
              className={`h-6 w-6 transition-transform ${isLoading ? 'scale-90 opacity-70' : 'scale-100'}`}
            />
          </button>
        </form>
        <div className="max-w-3xl mx-auto mt-4 flex items-center gap-3">
          <button
            onClick={toggleRL}
            disabled={isLoading}
            className={`h-14 w-14 md:h-[60px] md:w-[60px] flex-shrink-0 flex items-center justify-center ${isBrainActive ? 'bg-purple-600' : 'bg-gray-800'} hover:opacity-90 active:opacity-80 text-white rounded-full shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed transition-all touch-manipulation select-none relative`}
            title={isBrainActive ? 'AI Learning Active' : 'Enable AI Learning'}
          >
            <Brain
              className={`h-6 w-6 transition-all ${isTraining ? 'animate-pulse text-purple-400' : ''}`}
            />
            {isTraining && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full h-full rounded-full border-4 border-t-purple-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
              </div>
            )}
            {!isTraining && experiences > 0 && !isBrainActive && (
              <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {experiences}
              </div>
            )}
            {isBrainActive && (
              <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs font-medium px-2 py-0.5 rounded-full animate-pulse">
                ON
              </div>
            )}
          </button>
          {mode === 'finance' && (
            <button
              type="button"
              onClick={async () => {
                try {
                  setIsTraining(true);
                  if (!canToggleBrain) {
                    toast.error('Please wait for the current response to complete');
                    return;
                  }
                  setIsBrainActive(!isBrainActive);
                  const button = document.querySelector('.brain-button svg');
                  if (button) {
                    button.style.stroke = isBrainActive ? 'currentColor' : '#3b82f6';
                  }
                  if (!isBrainActive) {
                    setTrainingProgress(0);
                    const progressInterval = setInterval(() => {
                      setTrainingProgress(prev => {
                        if (prev >= 100) {
                          clearInterval(progressInterval);
                          return 100;
                        }
                        return prev + 10;
                      });
                    }, 500);
                    await trainRLModel(trainedModel);
                    toast.success('AI training started successfully');
                    setTimeout(() => {
                      setIsTraining(false);
                      setTrainingProgress(0);
                      toast.success('Training completed! AI model updated with new experiences');
                    }, 5000);
                  } else {
                    setIsTraining(false);
                    setTrainingProgress(0);
                    toast.success('AI training deactivated');
                  }
                } catch (error) {
                  toast.error(
                    error instanceof Error ? error.message : 'Failed to start training'
                  );
                  setIsTraining(false);
                  setTrainingProgress(0);
                  setIsBrainActive(false);
                }
              }}
              className={`brain-button h-14 w-14 md:h-[60px] md:w-[60px] flex-shrink-0 flex items-center justify-center bg-gray-800 hover:bg-gray-900 active:bg-gray-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all touch-manipulation select-none relative ${isTraining ? 'cursor-not-allowed opacity-80' : ''} ${isBrainActive ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-purple-600' : ''} ${!canToggleBrain ? 'cursor-not-allowed opacity-50' : ''}`}
              title="Train AI on Advanced Finances"
              disabled={isTraining || !canToggleBrain}
            >
              {isTraining && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        className="text-gray-700"
                        strokeWidth="8"
                        stroke="currentColor"
                        fill="transparent"
                        r="46"
                        cx="50"
                        cy="50"
                      />
                      <circle
                        className="text-purple-500 transition-all duration-300"
                        strokeWidth="8"
                        strokeDasharray={`${
                          (2 * Math.PI * 46 * trainingProgress) / 100
                        } ${2 * Math.PI * 46}`}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="46"
                        cx="50"
                        cy="50"
                        style={{
                          transform: 'rotate(-90deg)',
                          transformOrigin: '50% 50%',
                        }}
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-medium">{trainingProgress}%</span>
                </div>
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-colors duration-300 ${isTraining ? 'opacity-0' : 'opacity-100'}`}
                style={{ stroke: isBrainActive ? '#9333ea' : 'currentColor' }}
              >
                <path d="M9.5 2a2.5 2.5 0 1 1 0 5a2.5 2.5 0 0 1 0-5Z" />
                <path d="M14.5 2a2.5 2.5 0 1 1 0 5a2.5 2.5 0 0 1 0-5Z" />
                <path d="M3 7a2 2 0 0 0-2 2v1c0 4.1 2.5 7.2 6 7.9v2.1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2.1c3.5-.7 6-3.8 6-7.9V9a2 2 0 0 0-2-2" />
                <path d="M12 22v-6.5" />
              </svg>
              {!isTraining && experiences > 0 && !isBrainActive && (
                <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                  {experiences}
                </div>
              )}
              {isBrainActive && (
                <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs font-medium px-2 py-0.5 rounded-full animate-pulse">
                  ON
                </div>
              )}
            </button>
          )}
        </div>
        {isTraining && (
          <div className="mt-4 max-w-3xl mx-auto">
            <div className="bg-white/10 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-400 h-full transition-all duration-300 ease-out"
                style={{ width: `${trainingProgress}%` }}
              />
            </div>
            <p className="text-center text-white/70 text-sm mt-2">
              Training Progress: {trainingProgress}%
            </p>
          </div>
        )}
        <BankInformation isOpen={isBankModalOpen} onClose={() => setIsBankModalOpen(false)} />
        <p className="text-center text-white/70 text-sm mt-3">
          {isOnline
            ? 'Free financial advice for everyone • Powered by Angel'
            : 'You are currently offline • Check your connection'}
        </p>
      </div>
      {isPWA && isCameraOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white p-4 rounded-lg relative">
            <video ref={videoRef} autoPlay className="w-full h-auto rounded" />
            <div className="mt-2 flex justify-between">
              <button
                onClick={capturePhoto}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Capture
              </button>
              <button
                onClick={closeCamera}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatComponent;
